import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import {
  CartAddEvent,
  CartUpdateEvent,
  ThemeEvents,
  VariantSelectedEvent,
  VariantUpdateEvent,
} from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {Array<{id: number, option1: string | null, option2: string | null, option3: string | null, available: boolean}> | null} */
  #variantsPayload = null;
  /** @type {HTMLElement | null} */
  #hostProductCard = null;
  /** @type {((event: Event) => void) | null} */
  #cardVariantUpdateHandler;
  /** @type {((event: Event) => void) | null} */
  #variantSelectedHandler = null;
  /** @type {((event: Event) => void) | null} */
  #drawerOpenListener = null;
  /** @type {number | null} */
  #drawerOpenListenerTimeout = null;
  /** @type {HTMLFormElement | null} */
  #cachedForm = null;
  /** @type {HTMLInputElement | null} */
  #cachedVariantInput = null;
  /** @type {HTMLButtonElement[]} */
  #cachedSizeCells = [];
  /** @type {import('./product-form').AddToCartComponent | null} */
  #cachedAddContainer = null;
  /** @type {HTMLElement | null} */
  #cachedProductForm = null;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    const variantSelectedHandler = this.#updateQuickAddButtonState.bind(this);
    this.#variantSelectedHandler = variantSelectedHandler;
    document.addEventListener(ThemeEvents.variantSelected, variantSelectedHandler);
    this.addEventListener('click', this.#onSizeRailClick);

    const script = this.querySelector('script[data-size-rail-variants]');
    if (script?.textContent) {
      try {
        this.#variantsPayload = JSON.parse(script.textContent);
      } catch {
        this.#variantsPayload = null;
      }
    }

    this.#hostProductCard = this.closest('product-card');
    if (this.#hostProductCard && this.querySelector('[data-quick-add-size-rail]')) {
      this.#cardVariantUpdateHandler = this.#onCardVariantUpdate.bind(this);
      if (this.#cardVariantUpdateHandler) {
        this.#hostProductCard.addEventListener(ThemeEvents.variantUpdate, this.#cardVariantUpdateHandler);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    if (this.#variantSelectedHandler) {
      document.removeEventListener(ThemeEvents.variantSelected, this.#variantSelectedHandler);
    }
    this.#variantSelectedHandler = null;
    this.#clearDrawerOpenListener();
    this.removeEventListener('click', this.#onSizeRailClick);
    if (this.#hostProductCard && typeof this.#cardVariantUpdateHandler === 'function') {
      this.#hostProductCard.removeEventListener(ThemeEvents.variantUpdate, this.#cardVariantUpdateHandler);
    }
    this.#hostProductCard = null;
    this.#cardVariantUpdateHandler = null;
    this.#cachedForm = null;
    this.#cachedVariantInput = null;
    this.#cachedSizeCells = [];
    this.#cachedAddContainer = null;
    this.#cachedProductForm = null;
  }

  #clearDrawerOpenListener() {
    if (this.#drawerOpenListener) {
      document.removeEventListener(CartAddEvent.eventName, this.#drawerOpenListener);
      this.#drawerOpenListener = null;
    }
    if (this.#drawerOpenListenerTimeout) {
      window.clearTimeout(this.#drawerOpenListenerTimeout);
      this.#drawerOpenListenerTimeout = null;
    }
  }

  #getForm() {
    if (this.#cachedForm && this.contains(this.#cachedForm)) {
      return this.#cachedForm;
    }
    const form = this.querySelector('form');
    this.#cachedForm = form instanceof HTMLFormElement ? form : null;
    return this.#cachedForm;
  }

  #getVariantInput() {
    if (this.#cachedVariantInput && this.contains(this.#cachedVariantInput)) {
      return this.#cachedVariantInput;
    }
    const variantInput = this.querySelector('input[name="id"]');
    this.#cachedVariantInput = variantInput instanceof HTMLInputElement ? variantInput : null;
    return this.#cachedVariantInput;
  }

  #getSizeCells() {
    if (
      this.#cachedSizeCells.length > 0 &&
      this.#cachedSizeCells.every((cell) => this.contains(cell))
    ) {
      return this.#cachedSizeCells;
    }
    this.#cachedSizeCells = Array.from(this.querySelectorAll('.quick-add__size-cell')).filter(
      (cell) => cell instanceof HTMLButtonElement
    );
    return this.#cachedSizeCells;
  }

  #getAddContainer() {
    if (this.#cachedAddContainer && this.contains(this.#cachedAddContainer)) {
      return this.#cachedAddContainer;
    }
    const addContainer = this.querySelector('add-to-cart-component');
    this.#cachedAddContainer = addContainer ? /** @type {import('./product-form').AddToCartComponent} */ (addContainer) : null;
    return this.#cachedAddContainer;
  }

  #getProductForm() {
    if (this.#cachedProductForm && this.contains(this.#cachedProductForm)) {
      return this.#cachedProductForm;
    }
    const productForm = this.querySelector('product-form-component');
    this.#cachedProductForm = productForm instanceof HTMLElement ? productForm : null;
    return this.#cachedProductForm;
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onCardVariantUpdate(event) {
    const rail = this.querySelector('[data-quick-add-size-rail]');
    if (!(rail instanceof HTMLElement) || !this.#variantsPayload?.length) return;

    const resource = event.detail?.resource;
    if (!resource?.id) return;

    const sizePos = Number(rail.dataset.sizeOptionPosition);
    if (!sizePos || Number.isNaN(sizePos)) return;

    const anchorSource = /** @type {Record<string, unknown>} */ (resource);
    const anchorVariant = {
      option1: /** @type {string | null} */ (anchorSource.option1 ?? null),
      option2: /** @type {string | null} */ (anchorSource.option2 ?? null),
      option3: /** @type {string | null} */ (anchorSource.option3 ?? null),
    };
    this.#syncSizeRailButtons(anchorVariant, sizePos);
  }

  /**
   * @param {{ option1?: string | null, option2?: string | null, option3?: string | null }} anchorVariant
   * @param {number} sizePos
   */
  #syncSizeRailButtons(anchorVariant, sizePos) {
    const buttons = this.#getSizeCells();
    let selectedSizeValue = null;
    if (sizePos === 1) selectedSizeValue = anchorVariant?.option1 ?? null;
    if (sizePos === 2) selectedSizeValue = anchorVariant?.option2 ?? null;
    if (sizePos === 3) selectedSizeValue = anchorVariant?.option3 ?? null;
    for (const btn of buttons) {
      const sizeVal = btn.dataset.sizeValue ?? '';
      const match = this.#findVariantForSize(this.#variantsPayload ?? [], anchorVariant, sizePos, sizeVal);
      if (match) {
        btn.dataset.variantId = String(match.id);
        btn.disabled = !match.available;
      } else {
        btn.disabled = true;
      }

      if (selectedSizeValue && sizeVal === selectedSizeValue) {
        btn.dataset.selected = 'true';
      } else {
        btn.removeAttribute('data-selected');
      }
    }
  }

  /**
   * @param {Array<Record<string, unknown>>} variants
   * @param {Record<string, unknown>} anchor
   * @param {number} sizePos
   * @param {string} sizeVal
   */
  #findVariantForSize(variants, anchor, sizePos, sizeVal) {
    const sKey = `option${sizePos}`;
    return /** @type {typeof variants[0] | undefined} */ (
      variants.find((v) => {
        if (v[sKey] !== sizeVal) return false;
        for (let i = 1; i <= 3; i++) {
          if (i === sizePos) continue;
          const k = `option${i}`;
          const av = anchor[k] ?? null;
          const vv = v[k] ?? null;
          if (vv !== av) return false;
        }
        return true;
      })
    );
  }

  /**
   * @param {MouseEvent} event
   */
  #onSizeRailClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const cell = target.closest('.quick-add__size-cell');
    if (!cell || !(cell instanceof HTMLButtonElement) || cell.disabled) return;

    event.preventDefault();
    event.stopPropagation();

    const variantId = cell.dataset.variantId;
    if (!variantId) return;

    const form = this.#getForm();
    const variantInput = this.#getVariantInput();
    if (!form || !variantInput) return;

    variantInput.value = variantId;
    variantInput.removeAttribute('disabled');
    this.#getSizeCells().forEach((button) => {
      if (button === cell) {
        button.dataset.selected = 'true';
      } else {
        button.removeAttribute('data-selected');
      }
    });

    const addContainer = this.#getAddContainer();
    if (addContainer && typeof addContainer.enable === 'function') {
      addContainer.enable();
    }

    const productCard = /** @type {HTMLElement | null} */ (this.closest('product-card'));
    const productForm = this.#getProductForm();
    const productId = productForm?.dataset?.productId;

    /** Open cart drawer after a successful add from the grid (mirrors Nuco auto-open drawer UX). */
    if (productCard && productId) {
      this.#clearDrawerOpenListener();
      /** @param {Event} cartEvent */
      const openDrawerOnSuccess = (cartEvent) => {
        const detail = /** @type {CustomEvent} */ (cartEvent).detail;
        if (detail?.data?.didError) return;
        if (
          detail?.data?.productId !== undefined &&
          String(detail.data.productId) !== String(productId)
        ) {
          return;
        }
        this.#clearDrawerOpenListener();
        const drawer = document.querySelector('cart-drawer-component');
        if (drawer && 'open' in drawer && typeof drawer.open === 'function') {
          drawer.open();
        }
      };
      this.#drawerOpenListener = openDrawerOnSuccess;
      document.addEventListener(CartAddEvent.eventName, openDrawerOnSuccess);
      this.#drawerOpenListenerTimeout = window.setTimeout(() => {
        this.#clearDrawerOpenListener();
      }, 12000);
    }

    const addBtn = addContainer?.refs?.addToCartButton;
    if (addBtn instanceof HTMLButtonElement) {
      addBtn.disabled = false;
      queueMicrotask(() => addBtn.click());
    } else {
      form.requestSubmit();
    }
  };

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
